import { Link } from "@tanstack/react-router";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import type { BudgetLineItem } from "@/hooks/use-budgets";
import { extractBlockingCommitments } from "@/hooks/use-budgets";

interface DeleteBudgetLineDialogProps {
  lineItem: BudgetLineItem | null;
  error: Error | null;
  isPending: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

/**
 * Confirmation dialog for deleting a Buy Log line. If the server returns a
 * 409 Conflict with a `blockingCommitments` list, we swap the confirm CTA
 * for links to those commitments so the user can investigate before
 * deciding (AC#6).
 */
export function DeleteBudgetLineDialog({
  lineItem,
  error,
  isPending,
  onConfirm,
  onClose,
}: DeleteBudgetLineDialogProps) {
  const blocking = extractBlockingCommitments(error);
  const blocked = blocking !== null && blocking.length > 0;

  return (
    <AlertDialog
      open={lineItem !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {blocked ? "Can't delete this line" : "Delete this Buy Log line?"}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {blocked ? (
              <>
                This line has linked commitments and can't be deleted until they are voided or
                reassigned. Open the commitments below to decide next steps:
              </>
            ) : (
              <>
                This will remove{" "}
                <span className="font-medium">
                  {lineItem?.costCode?.code ?? lineItem?.description ?? "this line"}
                </span>{" "}
                and log an audit entry. This action cannot be undone.
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {blocked ? (
          <ul className="space-y-1 rounded border border-red-100 bg-red-50 p-3 text-sm">
            {blocking.map((c) => (
              <li key={c.id}>
                <Link
                  to="/commitments/$id"
                  params={{ id: c.id }}
                  className="font-medium text-red-800 hover:underline"
                >
                  {c.commitmentNumber}
                </Link>{" "}
                <span className="text-red-700">— {c.title}</span>
              </li>
            ))}
          </ul>
        ) : null}

        <AlertDialogFooter>
          <AlertDialogCancel onClick={onClose}>{blocked ? "Close" : "Cancel"}</AlertDialogCancel>
          {blocked ? null : (
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                onConfirm();
              }}
              disabled={isPending}
            >
              {isPending ? "Deleting…" : "Delete line"}
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
